/**
 * RecentDealsSection — the read-only seat's "what is the team working on".
 *
 * The five most recently updated deals (system-managed `ahoy_updated_at`),
 * each opening its record. Read through the CRM object store like every
 * other list, so it stays live over the WebSocket and shows exactly the
 * deals this principal may read.
 */

import { formatDistanceToNow } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { ButtonList } from '@/lib/base/button-list';
import { ListCard } from '@/lib/base/list-card';
import { Skeleton } from '@/lib/base/skeleton/skeleton';
import { CurrencyValueLabel } from '@/lib/crm/components/properties/labels/CurrencyValueLabel';
import { PipelineStageValueLabel } from '@/lib/crm/components/properties/labels/PipelineStageValueLabel';
import { useNavigateToEntity } from '@/lib/crm/hooks/useNavigateToEntity';
import { useCrmObjectList } from '@/lib/crm/store';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import type { CrmObject, CrmObjectFilters, QuantityValue } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

const RECENT_DEALS_LIMIT = 5;
const STAGE_ASSOCIATION = 'ahoy_deal_to_deal_pipeline_stage';

// Hoisted: stable query identity across renders.
const RECENT_DEALS_FILTERS: CrmObjectFilters = {
  properties: [
    'ahoy_display_name',
    'ahoy_name',
    'ahoy_amount',
    'ahoy_updated_at',
  ],
  sort: 'ahoy_updated_at',
  order: 'desc',
  page_size: RECENT_DEALS_LIMIT,
  include_total_count: false,
};
const RECENT_DEALS_OPTIONS = {
  include_associations: { [STAGE_ASSOCIATION]: { properties: [] } },
};

const SKELETON_ROWS = [0, 1, 2];

interface RecentDealsSectionProps {
  orgId: number;
}

function stageEntityIdOf(deal: CrmObject): string | undefined {
  return deal.associations?.find(
    a => a.association_type_name === STAGE_ASSOCIATION
  )?.target_entity_id;
}

function updatedAgo(deal: CrmObject): string | null {
  const raw = getPropertyValue<string>(deal.properties?.ahoy_updated_at);
  if (!raw) {
    return null;
  }
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return `Updated ${formatDistanceToNow(date, { addSuffix: true })}`;
}

interface RecentDealRowProps {
  deal: CrmObject;
  onOpen: (entityId: string, entityTypeName: string) => void;
}

function RecentDealRow({ deal, onOpen }: RecentDealRowProps) {
  const name = getDisplayName(deal);
  const ago = updatedAgo(deal);
  const stageId = stageEntityIdOf(deal);
  const entityId = deal.entity_id ?? '';

  const handleAction = useCallback(() => {
    onOpen(entityId, deal.entity_type_name);
  }, [onOpen, entityId, deal.entity_type_name]);

  return (
    // Stage and amount sit inside the row's own click target (not `trailing`,
    // which isolates clicks) — the whole row opens the deal.
    <ButtonList.Item
      onAction={handleAction}
      aria-label={`Open ${name}`}
      showChevron
    >
      <span className='flex items-center gap-3'>
        <span className='min-w-0 flex-1'>
          <span className='block truncate text-[13px] font-medium text-fg-primary'>
            {name}
          </span>
          {ago && (
            <span className='block text-[12px] text-fg-tertiary'>{ago}</span>
          )}
        </span>
        <span className='shrink-0'>
          <PipelineStageValueLabel
            value={stageId}
            fontSize='xs'
            interactive={false}
          />
        </span>
        <span className='w-24 shrink-0 text-right font-medium tabular-nums text-fg-primary'>
          <CurrencyValueLabel
            value={getPropertyValue<QuantityValue>(
              deal.properties?.ahoy_amount
            )}
            fontSize='table'
          />
        </span>
      </span>
    </ButtonList.Item>
  );
}

function RecentDealsSkeleton() {
  return (
    <ListCard background='primary' aria-busy='true'>
      {SKELETON_ROWS.map(row => (
        <div key={row} className='flex items-center gap-3 px-4 py-3'>
          <span className='flex flex-1 flex-col gap-1.5'>
            <Skeleton className='h-3.5 w-2/5' />
            <Skeleton className='h-3 w-1/4' />
          </span>
          <Skeleton className='h-5 w-20' />
          <Skeleton className='h-3.5 w-16' />
        </div>
      ))}
    </ListCard>
  );
}

export function RecentDealsSection({ orgId }: RecentDealsSectionProps) {
  const { data, isLoading, error } = useCrmObjectList(
    orgId,
    'ahoy_deal',
    RECENT_DEALS_FILTERS,
    RECENT_DEALS_OPTIONS
  );
  const deals = useMemo(() => data?.results ?? [], [data]);
  const navigateToEntity = useNavigateToEntity();
  const handleOpen = useCallback(
    (entityId: string, entityTypeName: string) => {
      navigateToEntity(entityId, entityTypeName);
    },
    [navigateToEntity]
  );

  let body: React.ReactNode;
  if (isLoading) {
    body = <RecentDealsSkeleton />;
  } else if (error) {
    body = (
      <ListCard background='primary'>
        <p
          role='alert'
          className='px-4 py-6 text-center text-[13px] text-fg-tertiary'
        >
          We couldn&apos;t load recent deals. Try again later.
        </p>
      </ListCard>
    );
  } else if (deals.length === 0) {
    body = (
      <ListCard background='primary'>
        <p className='px-4 py-6 text-center text-[13px] text-fg-tertiary'>
          No deals yet.
        </p>
      </ListCard>
    );
  } else {
    body = (
      <ButtonList>
        {deals.map(deal => (
          <RecentDealRow key={deal.entity_id} deal={deal} onOpen={handleOpen} />
        ))}
      </ButtonList>
    );
  }

  return (
    <section aria-label='Recently updated deals' className='space-y-3'>
      <div className='flex items-center justify-between'>
        <h2 className='text-[14px] font-semibold text-fg-primary'>
          Recently updated deals
        </h2>
        <Link
          to={`/${orgId}/crm/deals`}
          className='text-[12px] font-semibold text-fg-primary transition-colors hover:text-fg-tertiary'
        >
          See all deals
        </Link>
      </div>
      {body}
    </section>
  );
}
